import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, X } from 'lucide-react';
import axios from 'axios';
import { RouteTransitionLogo } from '../../components/RouteTransitionLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FONT = "'Inria Serif', serif";

const formatEuro = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('en-GB', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 });
};

function HeadlineRow({ label, value, valueColor, big }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <span style={{ fontSize: big ? 16 : 14, color: 'var(--text-2)', fontWeight: big ? 600 : 500 }}>{label}</span>
      <span style={{ fontFamily: FONT, fontSize: big ? 26 : 18, fontWeight: big ? 700 : 600, color: valueColor || 'var(--text)' }}>{value}</span>
    </div>
  );
}

function DetailRow({ label, value, valueColor }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <span style={{ fontSize: 13, color: 'var(--text-3)' }}>{label}</span>
      <span style={{ fontFamily: FONT, fontSize: 14, fontWeight: 500, color: valueColor || 'var(--text)' }}>{value}</span>
    </div>
  );
}

export default function DemoCompletePage() {
  const { txnId } = useParams();
  const navigate = useNavigate();
  const [txn, setTxn] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const fetchTxn = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/demo-transactions/${txnId}`, { headers: { Authorization: `Bearer ${token}` } });
      setTxn(res.data);
    } catch (_) {
      /* ignore */
    }
  }, [txnId]);

  useEffect(() => { fetchTxn(); }, [fetchTxn]);

  if (!txn) {
    return (
      <div className="app-shell" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--app-bg)' }}>
        <p style={{ fontFamily: FONT, fontSize: 18, color: 'var(--text-3)' }}>Loading…</p>
      </div>
    );
  }

  const feesPaid = Number(txn.viva_wallet_fee || 0) + Number(txn.app_fee || 0) + Number(txn.host_commission || 0);

  return (
    <div className="app-shell" data-testid="demo-complete-page" style={{ minHeight: '100vh', backgroundColor: 'var(--app-bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: 40, paddingLeft: 24, paddingRight: 24, position: 'relative' }}>
      <button
        type="button"
        onClick={() => navigate('/demo-scan')}
        aria-label="Close demo"
        data-testid="demo-complete-close"
        style={{ position: 'absolute', top: 'max(14px, env(safe-area-inset-top))', right: 18, width: 40, height: 40, borderRadius: '50%', background: 'var(--surface)', border: '1px solid var(--card-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 2 }}
      >
        <X size={20} color="var(--text)" />
      </button>

      <div style={{ marginTop: 36 }}>
        <RouteTransitionLogo width={110} height={98} dataTestId="demo-complete-logo" />
      </div>

      <motion.div
        initial={{ scale: 0.4, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 260, damping: 18, delay: 0.05 }}
        style={{ marginTop: 20, width: 76, height: 76, borderRadius: '50%', backgroundColor: 'var(--status-success-text)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 14px 30px rgba(31, 142, 42, 0.28)' }}
      >
        <svg width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 19.5L16 26.5L29 12.5" stroke="#FFFFFF" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </motion.div>

      <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 700, color: 'var(--text)', textAlign: 'center', marginTop: 18, marginBottom: 4 }}>
        Demo payment received
      </h2>
      <p style={{ fontSize: 13, color: 'var(--text-3)', margin: 0 }}>
        Customer ID · <span style={{ fontFamily: 'monospace', letterSpacing: 1 }}>{txn.customer_id || '—'}</span>
      </p>

      <div data-testid="demo-complete-headline" style={{ marginTop: 22, width: '100%', maxWidth: 360, background: 'var(--surface)', border: '1px solid var(--card-border)', borderRadius: 22, padding: 20, boxShadow: '0 8px 24px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <HeadlineRow label="Profit" value={formatEuro(txn.venue_profit)} valueColor="var(--status-success-text)" big />
        <HeadlineRow label="Fees paid" value={formatEuro(feesPaid)} />
        <HeadlineRow label="Staff tips" value={txn.tip_amount > 0 ? formatEuro(txn.tip_amount) : '—'} valueColor={txn.tip_amount > 0 ? 'var(--status-success-text)' : 'var(--text-3)'} />

        <button
          type="button"
          data-testid="demo-complete-expand-btn"
          onClick={() => setExpanded((v) => !v)}
          style={{ marginTop: 4, background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--accent-purple)', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '6px 0' }}
        >
          {expanded ? 'Hide details' : 'See full breakdown'}
          <motion.span animate={{ rotate: expanded ? 180 : 0 }} transition={{ duration: 0.18 }} style={{ display: 'inline-flex' }}>
            <ChevronDown size={16} />
          </motion.span>
        </button>

        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div
              key="demo-expanded"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.22 }}
              style={{ overflow: 'hidden' }}
              data-testid="demo-complete-expanded"
            >
              <div style={{ height: 1, background: 'var(--hairline)', margin: '4px 0 12px' }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <DetailRow label="Original bill" value={formatEuro(txn.original_amount)} />
                <DetailRow label={`Allure discount · ${Math.round(txn.discount_percent || 0)}%`} value={`− ${formatEuro((txn.original_amount || 0) - (txn.discounted_amount || 0))}`} valueColor="var(--status-success-text)" />
                <DetailRow label="Customer paid" value={formatEuro(txn.discounted_amount)} />
                <div style={{ height: 1, background: 'var(--hairline)', margin: '2px 0' }} />
                <DetailRow label="Viva Wallet fee" value={`− ${formatEuro(txn.viva_wallet_fee)}`} />
                <DetailRow label="Allure fee" value={`− ${formatEuro(txn.app_fee)}`} />
                <div style={{ height: 1, background: 'var(--hairline)', margin: '2px 0' }} />
                <DetailRow label="Net to your account" value={formatEuro(txn.venue_profit)} valueColor="var(--status-success-text)" />
                {txn.tip_amount > 0 && (
                  <DetailRow label={`Staff tip`} value={`+ ${formatEuro(txn.tip_amount)}`} valueColor="var(--status-success-text)" />
                )}
                {txn.payment_reference && (
                  <p style={{ fontSize: 11, color: 'var(--text-3)', margin: '6px 0 0', textAlign: 'right' }}>
                    Ref: <span style={{ fontFamily: 'monospace' }}>{txn.payment_reference}</span>
                  </p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <button
        type="button"
        data-testid="demo-complete-new-scan-btn"
        onClick={() => navigate('/demo-scan/scan')}
        style={{ marginTop: 22, width: '100%', maxWidth: 360, height: 50, background: 'var(--solid-cta-bg)', color: 'var(--solid-cta-text)', border: 'none', borderRadius: 999, fontSize: 15, fontWeight: 600, cursor: 'pointer', boxShadow: '0 8px 22px rgba(0,0,0,0.18)' }}
      >
        New demo scan
      </button>

      <p style={{ marginTop: 16, fontFamily: FONT, fontSize: 11, color: 'var(--text-3)', textAlign: 'center' }}>
        Demo mode · No real payment was processed.
      </p>
    </div>
  );
}
